/**
 * Per-transaction notes for the mobile swipe deck.
 * Ephemeral and scoped by user:household. Notes may contain financial context,
 * so do not persist them in browser storage.
 */
const STORAGE_KEY_PREFIX = 'transaction-notes:v2:';
const LEGACY_STORAGE_KEY = 'transaction-notes:v1';
const scopedNotes = new Map();

const hasLocalStorage = () => typeof localStorage !== 'undefined';

const normalizeScopeKey = (scopeKey) => {
  if (!scopeKey) return null;
  const normalized = String(scopeKey).trim();
  return normalized || null;
};
const buildStorageKey = (scopeKey) => `${STORAGE_KEY_PREFIX}${normalizeScopeKey(scopeKey)}`;

const load = (scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) {
    return {};
  }
  return { ...(scopedNotes.get(normalizedScopeKey) || {}) };
};

const save = (data, scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return;

  scopedNotes.set(normalizedScopeKey, { ...data });
};

export const buildNotesScopeKey = (userId, householdId) => {
  if (!userId || !householdId) return null;
  return `${userId}:${householdId}`;
};

/** Get the note for a transaction (empty string if none) */
export const getNote = (txId, scopeKey) => {
  const data = load(scopeKey);
  return data[String(txId)] || '';
};

/** Save a note (max 30 chars). Empty string removes the note. */
export const saveNote = (txId, note, scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return '';

  const data = load(scopeKey);
  const trimmed = (note || '').trim().slice(0, 30);
  if (trimmed) {
    data[String(txId)] = trimmed;
  } else {
    delete data[String(txId)];
  }
  save(data, scopeKey);
  return trimmed;
};

/** Get all notes as { [txId]: noteString } */
export const getAllNotes = (scopeKey) => load(scopeKey);

export const clearNotesForScope = (scopeKey) => {
  const normalizedScopeKey = normalizeScopeKey(scopeKey);
  if (!normalizedScopeKey) return;

  scopedNotes.delete(normalizedScopeKey);
  if (hasLocalStorage()) {
    localStorage.removeItem(buildStorageKey(normalizedScopeKey));
  }
};

export const clearAllNotes = () => {
  scopedNotes.clear();
  if (!hasLocalStorage()) return;

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(STORAGE_KEY_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
};

export const clearLegacyNotes = () => {
  if (!hasLocalStorage()) return;

  localStorage.removeItem(LEGACY_STORAGE_KEY);
};
